import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    DollarSign, TrendingUp, FileText, CreditCard, Plus, Filter, RefreshCw,
    AlertTriangle, Calendar, Building2, ChevronRight, ChevronDown, X,
    CheckCircle2, Clock, Ban, Star, Users, Edit, Banknote, Receipt,
    ArrowUpRight, Download, Search
} from 'lucide-react';
import { apiFetch } from '../services/cloudflareService';

interface BillingClient {
    id: string; type: string; hospitalId?: string; groupId?: string;
    businessName: string; tradeName?: string; rfc?: string; cfdiUse?: string;
    taxRegime?: string; billingEmail?: string; billingAddress?: string;
    billingCity?: string; billingState?: string; billingZip?: string;
    billingCountry?: string; contactName?: string; contactPhone?: string;
    contactEmail?: string; currency: string; notes?: string; isActive?: boolean;
}

interface BillingContract {
    id: string; clientId: string; name: string; tier: string; billingCycle: string;
    baseAmount: number; discountPercent: number; discountReason?: string;
    finalAmount: number; currency: string; startDate: string; endDate: string;
    autoRenew?: boolean; status: string; hospitalsIncluded?: number;
    notes?: string; clientName?: string; clientType?: string;
}

interface BillingInvoice {
    id: string; invoiceNumber: string; contractId: string; clientId: string;
    periodStart: string; periodEnd: string; periodLabel?: string;
    subtotal: number; tax: number; total: number; currency: string;
    status: string; dueDate: string; paidDate?: string; paidAmount: number;
    notes?: string; clientName?: string; contractName?: string;
}

interface BillingSummary {
    activeContracts: number; mrr: number;
    pendingInvoices: { count: number; amount: number };
    overdueInvoices: { count: number; amount: number };
    totalRevenue: number; expiringContracts: number;
}

interface Props {
    theme: 'dark' | 'light';
    hospitals: any[];
    groups: any[];
    onNavigate?: (tab: string) => void;
}

const TIER_LABELS: Record<string, string> = { clinic: 'Clínica', hospital: 'Hospital', chain: 'Cadena' };
const CYCLE_LABELS: Record<string, string> = { monthly: 'Mensual', quarterly: 'Trimestral', annual: 'Anual' };
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    paused: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
    cancelled: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
    expired: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20' },
    pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
    sent: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
    paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    partial: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
    overdue: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
    draft: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20' },
};
const STATUS_LABELS: Record<string, string> = {
    active: 'Activo', paused: 'Pausado', cancelled: 'Cancelado', expired: 'Vencido',
    pending: 'Pendiente', sent: 'Enviada', paid: 'Pagada', partial: 'Parcial',
    overdue: 'Vencida', draft: 'Borrador', credited: 'Acreditada',
};
const METHOD_LABELS: Record<string, string> = { transfer: 'Transferencia', cash: 'Efectivo', card: 'Tarjeta', check: 'Cheque', other: 'Otro' };

const formatCurrency = (amount: number, currency = 'MXN') =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const BillingDashboard: React.FC<Props> = ({ theme, hospitals, groups, onNavigate }) => {
    const [activeView, setActiveView] = useState<'overview' | 'contracts' | 'invoices' | 'clients'>('overview');
    const [summary, setSummary] = useState<BillingSummary | null>(null);
    const [clients, setClients] = useState<BillingClient[]>([]);
    const [contracts, setContracts] = useState<BillingContract[]>([]);
    const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Modal states
    const [showClientModal, setShowClientModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [editingClient, setEditingClient] = useState<BillingClient | null>(null);
    const [editingContract, setEditingContract] = useState<BillingContract | null>(null);
    const [editingInvoice, setEditingInvoice] = useState<BillingInvoice | null>(null);
    const [paymentInvoice, setPaymentInvoice] = useState<BillingInvoice | null>(null);

    // Form states
    const [clientForm, setClientForm] = useState<Partial<BillingClient>>({ type: 'hospital', currency: 'MXN', billingCountry: 'MX', cfdiUse: 'G03' });
    const [contractForm, setContractForm] = useState<any>({ tier: 'hospital', billingCycle: 'monthly', baseAmount: 35000, discountPercent: 0, currency: 'MXN', autoRenew: true, status: 'active', hospitalsIncluded: 1 });
    const [invoiceForm, setInvoiceForm] = useState<any>({ status: 'pending', tax: 0 });
    const [paymentForm, setPaymentForm] = useState<any>({ method: 'transfer' });

    const t = useMemo(() => ({
        textMain: theme === 'dark' ? 'text-slate-300' : 'text-slate-600',
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        cardShadow: theme === 'dark' ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]' : 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
        inputBg: theme === 'dark' ? 'bg-black/20' : 'bg-slate-100/50',
        modalBg: theme === 'dark' ? 'bg-slate-900' : 'bg-white',
        modalBorder: theme === 'dark' ? 'border-white/10' : 'border-slate-200',
    }), [theme]);

    const cardClass = `${t.cardBg} rounded-2xl border ${t.cardBorder} ${t.cardShadow} p-6 relative overflow-hidden`;
    const inputClass = `w-full ${t.inputBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textWhite} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`;
    const labelClass = `text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`;

    const fetchAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const [sumRes, clientsRes, contractsRes, invoicesRes] = await Promise.all([
                apiFetch('/api/billing/summary', { credentials: 'include' }),
                apiFetch('/api/billing/clients', { credentials: 'include' }),
                apiFetch('/api/billing/contracts', { credentials: 'include' }),
                apiFetch('/api/billing/invoices', { credentials: 'include' }),
            ]);
            if (sumRes.ok) setSummary(await sumRes.json());
            if (clientsRes.ok) setClients(await clientsRes.json());
            if (contractsRes.ok) setContracts(await contractsRes.json());
            if (invoicesRes.ok) setInvoices(await invoicesRes.json());
        } catch { }
        setIsLoading(false);
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Handlers ──
    const saveClient = async () => {
        const method = editingClient ? 'PUT' : 'POST';
        const url = editingClient ? `/api/billing/clients/${editingClient.id}` : '/api/billing/clients';
        await apiFetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(clientForm) });
        setShowClientModal(false); setEditingClient(null);
        setClientForm({ type: 'hospital', currency: 'MXN', billingCountry: 'MX', cfdiUse: 'G03' });
        fetchAll();
    };

    const saveContract = async () => {
        const method = editingContract ? 'PUT' : 'POST';
        const url = editingContract ? `/api/billing/contracts/${editingContract.id}` : '/api/billing/contracts';
        await apiFetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(contractForm) });
        setShowContractModal(false); setEditingContract(null);
        setContractForm({ tier: 'hospital', billingCycle: 'monthly', baseAmount: 35000, discountPercent: 0, currency: 'MXN', autoRenew: true, status: 'active', hospitalsIncluded: 1 });
        fetchAll();
    };

    const saveInvoice = async () => {
        await apiFetch('/api/billing/invoices', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(invoiceForm) });
        setShowInvoiceModal(false);
        setInvoiceForm({ status: 'pending', tax: 0 });
        fetchAll();
    };

    const savePayment = async () => {
        await apiFetch('/api/billing/payments', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...paymentForm, invoiceId: paymentInvoice?.id }) });
        setShowPaymentModal(false); setPaymentInvoice(null);
        setPaymentForm({ method: 'transfer' });
        fetchAll();
    };

    const updateInvoiceStatus = async (id: string, status: string) => {
        await apiFetch(`/api/billing/invoices/${id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
        fetchAll();
    };

    const renderStatusBadge = (status: string) => {
        const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${colors.bg} ${colors.text} border ${colors.border}`}>
                {STATUS_LABELS[status] || status}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-8 w-48 bg-indigo-500/20 rounded-lg" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-indigo-500/10 rounded-2xl" />)}
                </div>
            </div>
        );
    }

    const pendingCount = invoices.filter(i => ['pending', 'sent', 'partial', 'overdue'].includes(i.status)).length;

    return (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'} shadow-[0_0_15px_rgba(16,185,129,0.15)]`}>
                        <DollarSign className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div>
                        <h2 className={`text-xl font-black ${t.textWhite} leading-tight`}>Finanzas</h2>
                        <p className={`text-xs ${t.textMuted} font-medium`}>Contratos, facturación y cobros</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Sub-navigation */}
                    {(['overview', 'contracts', 'invoices', 'clients'] as const).map(v => (
                        <button key={v} onClick={() => setActiveView(v)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activeView === v ? 'bg-indigo-600 text-white' : `${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}`}>
                            {{ overview: 'Resumen', contracts: 'Contratos', invoices: 'Facturas', clients: 'Clientes' }[v]}
                        </button>
                    ))}
                    <button onClick={fetchAll} className={`p-2 rounded-lg ${t.cardBg} border ${t.cardBorder} hover:bg-slate-700/50 transition-colors ml-2`}>
                        <RefreshCw className={`w-4 h-4 ${t.textMuted}`} />
                    </button>
                </div>
            </div>

            {/* ── OVERVIEW ── */}
            {activeView === 'overview' && (
                <>
                    {/* KPI Row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className={`${cardClass} group cursor-pointer hover:-translate-y-1 hover:shadow-xl transition-all`} onClick={() => setActiveView('contracts')}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                                <TrendingUp className="w-5 h-5" />
                            </div>
                            <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>{formatCurrency(summary?.mrr || 0)}</p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>MRR (Ingreso Mensual)</p>
                        </div>
                        <div className={`${cardClass} group cursor-pointer hover:-translate-y-1 hover:shadow-xl transition-all`} onClick={() => setActiveView('contracts')}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                                <FileText className="w-5 h-5" />
                            </div>
                            <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>{summary?.activeContracts || 0}</p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Contratos Activos</p>
                        </div>
                        <div className={`${cardClass} group cursor-pointer hover:-translate-y-1 hover:shadow-xl transition-all`} onClick={() => setActiveView('invoices')}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <p className={`text-3xl font-black ${(summary?.overdueInvoices?.count || 0) > 0 ? 'text-red-400' : t.textWhite} tracking-tight`}>
                                {formatCurrency((summary?.pendingInvoices?.amount || 0) + (summary?.overdueInvoices?.amount || 0))}
                            </p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Por Cobrar</p>
                        </div>
                        <div className={`${cardClass} group`}>
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-600 border border-blue-200'} w-fit mb-3 group-hover:scale-110 transition-transform`}>
                                <Banknote className="w-5 h-5" />
                            </div>
                            <p className={`text-3xl font-black ${t.textWhite} tracking-tight`}>{formatCurrency(summary?.totalRevenue || 0)}</p>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1`}>Revenue Total</p>
                        </div>
                    </div>

                    {/* Quick Actions + Alerts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Quick Actions */}
                        <div className={cardClass}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                                <Plus className="w-4 h-4 text-indigo-400" /> Acciones Rápidas
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { label: 'Nuevo Cliente', icon: Users, action: () => { setEditingClient(null); setClientForm({ type: 'hospital', currency: 'MXN', billingCountry: 'MX', cfdiUse: 'G03' }); setShowClientModal(true); } },
                                    { label: 'Nuevo Contrato', icon: FileText, action: () => { setEditingContract(null); setContractForm({ tier: 'hospital', billingCycle: 'monthly', baseAmount: 35000, discountPercent: 0, currency: 'MXN', autoRenew: true, status: 'active', hospitalsIncluded: 1 }); setShowContractModal(true); } },
                                    { label: 'Nueva Factura', icon: Receipt, action: () => { setInvoiceForm({ status: 'pending', tax: 0 }); setShowInvoiceModal(true); } },
                                    { label: 'Registrar Pago', icon: CreditCard, action: () => { if (invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').length > 0) { setPaymentInvoice(invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled')[0]); setShowPaymentModal(true); } } },
                                ].map(item => (
                                    <button key={item.label} onClick={item.action} className={`flex items-center gap-2 p-3 rounded-xl text-sm font-bold transition-all ${theme === 'dark' ? 'bg-white/[0.03] hover:bg-white/[0.06] text-slate-300 border border-white/[0.05]' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'}`}>
                                        <item.icon className="w-4 h-4 text-indigo-400" />
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Alerts */}
                        <div className={cardClass}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                                <AlertTriangle className="w-4 h-4 text-amber-400" /> Alertas
                            </h3>
                            <div className="space-y-2">
                                {(summary?.overdueInvoices?.count || 0) > 0 && (
                                    <button onClick={() => setActiveView('invoices')} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl ${theme === 'dark' ? 'bg-red-500/5 border border-red-500/20 hover:bg-red-500/10' : 'bg-red-50 border border-red-200 hover:bg-red-100'} transition-all`}>
                                        <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                                        <span className={`text-sm font-medium ${t.textWhite} flex-1`}>{summary?.overdueInvoices.count} factura{(summary?.overdueInvoices?.count || 0) > 1 ? 's' : ''} vencida{(summary?.overdueInvoices?.count || 0) > 1 ? 's' : ''}</span>
                                        <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />
                                    </button>
                                )}
                                {(summary?.expiringContracts || 0) > 0 && (
                                    <button onClick={() => setActiveView('contracts')} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl ${theme === 'dark' ? 'bg-amber-500/5 border border-amber-500/20 hover:bg-amber-500/10' : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'} transition-all`}>
                                        <div className="w-2 h-2 rounded-full bg-amber-400" />
                                        <span className={`text-sm font-medium ${t.textWhite} flex-1`}>{summary?.expiringContracts} contrato{(summary?.expiringContracts || 0) > 1 ? 's' : ''} por vencer (30 días)</span>
                                        <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />
                                    </button>
                                )}
                                {(summary?.overdueInvoices?.count || 0) === 0 && (summary?.expiringContracts || 0) === 0 && (
                                    <div className="flex flex-col items-center justify-center py-6 text-center">
                                        <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                                        <p className={`text-sm font-bold ${t.textWhite}`}>Todo en orden</p>
                                        <p className={`text-xs ${t.textMuted} mt-1`}>Sin alertas financieras</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Recent Contracts + Invoices Quick View */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className={cardClass}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} flex items-center gap-2`}>
                                    <FileText className="w-4 h-4 text-indigo-400" /> Contratos Recientes
                                </h3>
                                <button onClick={() => setActiveView('contracts')} className={`text-xs font-bold text-indigo-400 hover:text-indigo-300`}>Ver todos →</button>
                            </div>
                            {contracts.length === 0 ? (
                                <p className={`text-sm ${t.textMuted} text-center py-6`}>Sin contratos aún</p>
                            ) : (
                                <div className="space-y-2">
                                    {contracts.slice(0, 4).map(ct => (
                                        <div key={ct.id} className={`flex items-center justify-between p-3 rounded-xl ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-slate-50 border border-slate-100'}`}>
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-sm font-bold ${t.textWhite} truncate`}>{ct.name}</p>
                                                <p className={`text-[10px] ${t.textMuted}`}>{ct.clientName} • {CYCLE_LABELS[ct.billingCycle]}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0 ml-3">
                                                <p className={`text-sm font-black ${t.textWhite}`}>{formatCurrency(ct.finalAmount, ct.currency)}</p>
                                                {renderStatusBadge(ct.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={cardClass}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} flex items-center gap-2`}>
                                    <Receipt className="w-4 h-4 text-amber-400" /> Facturas Recientes
                                </h3>
                                <button onClick={() => setActiveView('invoices')} className={`text-xs font-bold text-indigo-400 hover:text-indigo-300`}>Ver todas →</button>
                            </div>
                            {invoices.length === 0 ? (
                                <p className={`text-sm ${t.textMuted} text-center py-6`}>Sin facturas aún</p>
                            ) : (
                                <div className="space-y-2">
                                    {invoices.slice(0, 4).map(inv => (
                                        <div key={inv.id} className={`flex items-center justify-between p-3 rounded-xl ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-slate-50 border border-slate-100'}`}>
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-sm font-bold ${t.textWhite} truncate`}>{inv.invoiceNumber}</p>
                                                <p className={`text-[10px] ${t.textMuted}`}>{inv.clientName} • Vence {new Date(inv.dueDate).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0 ml-3">
                                                <p className={`text-sm font-black ${t.textWhite}`}>{formatCurrency(inv.total, inv.currency)}</p>
                                                {renderStatusBadge(inv.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* ── CONTRACTS VIEW ── */}
            {activeView === 'contracts' && (
                <div className={cardClass}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className={`text-lg font-bold ${t.textWhite}`}>Contratos</h3>
                        <button onClick={() => { setEditingContract(null); setContractForm({ tier: 'hospital', billingCycle: 'monthly', baseAmount: 35000, discountPercent: 0, currency: 'MXN', autoRenew: true, status: 'active', hospitalsIncluded: 1 }); setShowContractModal(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 text-sm transition-all">
                            <Plus className="w-4 h-4" /> Nuevo Contrato
                        </button>
                    </div>
                    {contracts.length === 0 ? (
                        <p className={`text-sm ${t.textMuted} text-center py-12`}>Sin contratos. Crea uno para empezar a facturar.</p>
                    ) : (
                        <div className="space-y-3">
                            {contracts.map(ct => (
                                <div key={ct.id} className={`flex items-center gap-4 p-4 rounded-xl transition-all ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04]' : 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80'}`}>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-bold ${t.textWhite} truncate`}>{ct.name}</p>
                                        <p className={`text-xs ${t.textMuted}`}>
                                            {ct.clientName} • {TIER_LABELS[ct.tier]} • {CYCLE_LABELS[ct.billingCycle]}
                                            {ct.discountPercent > 0 && <span className="text-emerald-400 ml-1">(-{ct.discountPercent}%)</span>}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`text-lg font-black ${t.textWhite}`}>{formatCurrency(ct.finalAmount, ct.currency)}</p>
                                        <p className={`text-[10px] ${t.textMuted}`}>{new Date(ct.startDate).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })} → {new Date(ct.endDate).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })}</p>
                                    </div>
                                    <div className="flex-shrink-0">{renderStatusBadge(ct.status)}</div>
                                    <button onClick={() => { setEditingContract(ct); setContractForm(ct); setShowContractModal(true); }} className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-200'} transition-colors`}>
                                        <Edit className={`w-4 h-4 ${t.textMuted}`} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── INVOICES VIEW ── */}
            {activeView === 'invoices' && (
                <div className={cardClass}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className={`text-lg font-bold ${t.textWhite}`}>Facturas</h3>
                        <button onClick={() => { setInvoiceForm({ status: 'pending', tax: 0 }); setShowInvoiceModal(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 text-sm transition-all">
                            <Plus className="w-4 h-4" /> Nueva Factura
                        </button>
                    </div>
                    {invoices.length === 0 ? (
                        <p className={`text-sm ${t.textMuted} text-center py-12`}>Sin facturas. Crea una desde un contrato activo.</p>
                    ) : (
                        <div className="space-y-3">
                            {invoices.map(inv => (
                                <div key={inv.id} className={`flex items-center gap-4 p-4 rounded-xl transition-all ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04]' : 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80'}`}>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        inv.status === 'paid' ? (theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') :
                                        inv.status === 'overdue' ? (theme === 'dark' ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600') :
                                        (theme === 'dark' ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600')
                                    }`}>
                                        <Receipt className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-bold ${t.textWhite}`}>{inv.invoiceNumber}</p>
                                        <p className={`text-xs ${t.textMuted}`}>{inv.clientName} • {inv.periodLabel || `${inv.periodStart} - ${inv.periodEnd}`}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`text-lg font-black ${t.textWhite}`}>{formatCurrency(inv.total, inv.currency)}</p>
                                        {inv.paidAmount > 0 && inv.status !== 'paid' && (
                                            <p className={`text-[10px] text-emerald-400`}>Pagado: {formatCurrency(inv.paidAmount, inv.currency)}</p>
                                        )}
                                        <p className={`text-[10px] ${t.textMuted}`}>Vence: {new Date(inv.dueDate).toLocaleDateString('es-MX')}</p>
                                    </div>
                                    <div className="flex-shrink-0">{renderStatusBadge(inv.status)}</div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                            <button onClick={() => { setPaymentInvoice(inv); setPaymentForm({ method: 'transfer', amount: inv.total - inv.paidAmount, paymentDate: new Date().toISOString().split('T')[0] }); setShowPaymentModal(true); }} className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-emerald-500/10' : 'hover:bg-emerald-50'} transition-colors`} title="Registrar Pago">
                                                <CreditCard className="w-4 h-4 text-emerald-400" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── CLIENTS VIEW ── */}
            {activeView === 'clients' && (
                <div className={cardClass}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className={`text-lg font-bold ${t.textWhite}`}>Clientes de Facturación</h3>
                        <button onClick={() => { setEditingClient(null); setClientForm({ type: 'hospital', currency: 'MXN', billingCountry: 'MX', cfdiUse: 'G03' }); setShowClientModal(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 text-sm transition-all">
                            <Plus className="w-4 h-4" /> Nuevo Cliente
                        </button>
                    </div>
                    {clients.length === 0 ? (
                        <p className={`text-sm ${t.textMuted} text-center py-12`}>Sin clientes registrados. Agrega tu primer cliente para empezar.</p>
                    ) : (
                        <div className="space-y-3">
                            {clients.map(cl => (
                                <div key={cl.id} className={`flex items-center gap-4 p-4 rounded-xl transition-all ${theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04]' : 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80'}`}>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                        {cl.type === 'chain' ? <Building2 className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-bold ${t.textWhite} truncate`}>{cl.businessName}</p>
                                        <p className={`text-xs ${t.textMuted}`}>
                                            {TIER_LABELS[cl.type] || cl.type} • {cl.rfc || 'Sin RFC'} • {cl.currency}
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <p className={`text-xs ${t.textMuted}`}>{cl.contactName || '—'}</p>
                                        <p className={`text-[10px] ${t.textMuted}`}>{cl.billingEmail || '—'}</p>
                                    </div>
                                    <button onClick={() => { setEditingClient(cl); setClientForm(cl); setShowClientModal(true); }} className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-200'} transition-colors`}>
                                        <Edit className={`w-4 h-4 ${t.textMuted}`} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── MODALS ── */}
            {/* CLIENT MODAL */}
            <AnimatePresence>
                {showClientModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowClientModal(false)}>
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className={`${t.modalBg} border ${t.modalBorder} rounded-3xl w-full max-w-lg shadow-2xl max-h-[85vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
                            <div className={`p-6 border-b ${t.modalBorder} flex items-center justify-between sticky top-0 ${t.modalBg} z-10`}>
                                <h3 className={`text-lg font-bold ${t.textWhite}`}>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
                                <button onClick={() => setShowClientModal(false)} className={`p-1 rounded-lg ${t.textMuted} hover:text-white`}><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Tipo</label>
                                        <select value={clientForm.type} onChange={e => setClientForm({ ...clientForm, type: e.target.value })} className={inputClass}>
                                            <option value="clinic">Clínica</option>
                                            <option value="hospital">Hospital</option>
                                            <option value="chain">Cadena</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Moneda</label>
                                        <select value={clientForm.currency} onChange={e => setClientForm({ ...clientForm, currency: e.target.value })} className={inputClass}>
                                            <option value="MXN">MXN</option>
                                            <option value="USD">USD</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Razón Social *</label>
                                    <input value={clientForm.businessName || ''} onChange={e => setClientForm({ ...clientForm, businessName: e.target.value })} className={inputClass} placeholder="Ej: Christus Muguerza S.A. de C.V." />
                                </div>
                                <div>
                                    <label className={labelClass}>Nombre Comercial</label>
                                    <input value={clientForm.tradeName || ''} onChange={e => setClientForm({ ...clientForm, tradeName: e.target.value })} className={inputClass} placeholder="Ej: Christus Muguerza San Pedro" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>RFC</label>
                                        <input value={clientForm.rfc || ''} onChange={e => setClientForm({ ...clientForm, rfc: e.target.value.toUpperCase() })} className={inputClass} placeholder="XAXX010101AAA" maxLength={13} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Régimen Fiscal</label>
                                        <select value={clientForm.taxRegime || ''} onChange={e => setClientForm({ ...clientForm, taxRegime: e.target.value })} className={inputClass}>
                                            <option value="">Seleccionar</option>
                                            <option value="601">601 - General de LP Morales</option>
                                            <option value="603">603 - Personas Morales Sin Fines de Lucro</option>
                                            <option value="612">612 - Personas Físicas Empresarial</option>
                                            <option value="626">626 - Régimen Simplificado de Confianza</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Uso CFDI</label>
                                        <select value={clientForm.cfdiUse || 'G03'} onChange={e => setClientForm({ ...clientForm, cfdiUse: e.target.value })} className={inputClass}>
                                            <option value="G03">G03 - Gastos en general</option>
                                            <option value="G01">G01 - Adquisición de mercancías</option>
                                            <option value="P01">P01 - Por definir</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelClass}>CP Fiscal</label>
                                        <input value={clientForm.billingZip || ''} onChange={e => setClientForm({ ...clientForm, billingZip: e.target.value })} className={inputClass} placeholder="66260" maxLength={5} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Dirección Fiscal</label>
                                    <input value={clientForm.billingAddress || ''} onChange={e => setClientForm({ ...clientForm, billingAddress: e.target.value })} className={inputClass} placeholder="Av. Ejemplo 123, Col. Centro" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Ciudad</label>
                                        <input value={clientForm.billingCity || ''} onChange={e => setClientForm({ ...clientForm, billingCity: e.target.value })} className={inputClass} placeholder="Monterrey" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Estado</label>
                                        <input value={clientForm.billingState || ''} onChange={e => setClientForm({ ...clientForm, billingState: e.target.value })} className={inputClass} placeholder="Nuevo León" />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Email para Facturas</label>
                                    <input value={clientForm.billingEmail || ''} onChange={e => setClientForm({ ...clientForm, billingEmail: e.target.value })} className={inputClass} type="email" placeholder="facturacion@hospital.com" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Contacto de Pagos</label>
                                        <input value={clientForm.contactName || ''} onChange={e => setClientForm({ ...clientForm, contactName: e.target.value })} className={inputClass} placeholder="Nombre del contacto" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Teléfono Contacto</label>
                                        <input value={clientForm.contactPhone || ''} onChange={e => setClientForm({ ...clientForm, contactPhone: e.target.value })} className={inputClass} placeholder="81 1234 5678" />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Notas Internas</label>
                                    <textarea value={clientForm.notes || ''} onChange={e => setClientForm({ ...clientForm, notes: e.target.value })} className={`${inputClass} resize-none`} rows={2} placeholder="Notas internas sobre este cliente..." />
                                </div>
                            </div>
                            <div className={`p-6 pt-0 flex gap-3`}>
                                <button onClick={() => setShowClientModal(false)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>Cancelar</button>
                                <button onClick={saveClient} disabled={!clientForm.businessName} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50">
                                    {editingClient ? 'Guardar Cambios' : 'Crear Cliente'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* CONTRACT MODAL */}
            <AnimatePresence>
                {showContractModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowContractModal(false)}>
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className={`${t.modalBg} border ${t.modalBorder} rounded-3xl w-full max-w-lg shadow-2xl max-h-[85vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
                            <div className={`p-6 border-b ${t.modalBorder} flex items-center justify-between sticky top-0 ${t.modalBg} z-10`}>
                                <h3 className={`text-lg font-bold ${t.textWhite}`}>{editingContract ? 'Editar Contrato' : 'Nuevo Contrato'}</h3>
                                <button onClick={() => setShowContractModal(false)} className={`p-1 rounded-lg ${t.textMuted} hover:text-white`}><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className={labelClass}>Cliente *</label>
                                    <select value={contractForm.clientId || ''} onChange={e => setContractForm({ ...contractForm, clientId: e.target.value })} className={inputClass}>
                                        <option value="">Seleccionar cliente</option>
                                        {clients.map(c => <option key={c.id} value={c.id}>{c.businessName} ({TIER_LABELS[c.type]})</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Nombre del Contrato *</label>
                                    <input value={contractForm.name || ''} onChange={e => setContractForm({ ...contractForm, name: e.target.value })} className={inputClass} placeholder="Ej: Christus San Pedro - Mensual 2026" />
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className={labelClass}>Tier</label>
                                        <select value={contractForm.tier} onChange={e => {
                                            const tier = e.target.value;
                                            const baseAmount = tier === 'chain' ? 350000 : (tier === 'clinic' ? 12000 : 35000);
                                            setContractForm({ ...contractForm, tier, baseAmount });
                                        }} className={inputClass}>
                                            <option value="clinic">Clínica</option>
                                            <option value="hospital">Hospital</option>
                                            <option value="chain">Cadena Corporativa</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Ciclo</label>
                                        <select value={contractForm.billingCycle} onChange={e => setContractForm({ ...contractForm, billingCycle: e.target.value })} className={inputClass}>
                                            <option value="monthly">Mensual</option>
                                            <option value="quarterly">Trimestral</option>
                                            <option value="annual">Anual</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Moneda</label>
                                        <select value={contractForm.currency} onChange={e => setContractForm({ ...contractForm, currency: e.target.value })} className={inputClass}>
                                            <option value="MXN">MXN</option>
                                            <option value="USD">USD</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Precio Base</label>
                                        <input type="number" value={contractForm.baseAmount || ''} onChange={e => setContractForm({ ...contractForm, baseAmount: parseFloat(e.target.value) || 0 })} className={inputClass} placeholder="25000" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Descuento %</label>
                                        <input type="number" value={contractForm.discountPercent || ''} onChange={e => setContractForm({ ...contractForm, discountPercent: parseFloat(e.target.value) || 0 })} className={inputClass} placeholder="0" min="0" max="100" />
                                    </div>
                                </div>
                                {contractForm.discountPercent > 0 && (
                                    <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'} text-center`}>
                                        <p className="text-xs font-bold text-emerald-500">
                                            Monto final: {formatCurrency(contractForm.baseAmount * (1 - contractForm.discountPercent / 100), contractForm.currency)} / {CYCLE_LABELS[contractForm.billingCycle]}
                                        </p>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Inicio</label>
                                        <input type="date" value={contractForm.startDate || ''} onChange={e => setContractForm({ ...contractForm, startDate: e.target.value })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Fin</label>
                                        <input type="date" value={contractForm.endDate || ''} onChange={e => setContractForm({ ...contractForm, endDate: e.target.value })} className={inputClass} />
                                    </div>
                                </div>
                                {contractForm.tier === 'chain' && (
                                    <div>
                                        <label className={labelClass}>Hospitales Incluidos</label>
                                        <input type="number" value={contractForm.hospitalsIncluded || 1} onChange={e => setContractForm({ ...contractForm, hospitalsIncluded: parseInt(e.target.value) || 1 })} className={inputClass} min="1" />
                                    </div>
                                )}
                                <div>
                                    <label className={labelClass}>Estatus</label>
                                    <select value={contractForm.status} onChange={e => setContractForm({ ...contractForm, status: e.target.value })} className={inputClass}>
                                        <option value="active">Activo</option>
                                        <option value="paused">Pausado</option>
                                        <option value="cancelled">Cancelado</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Notas</label>
                                    <textarea value={contractForm.notes || ''} onChange={e => setContractForm({ ...contractForm, notes: e.target.value })} className={`${inputClass} resize-none`} rows={2} />
                                </div>
                            </div>
                            <div className={`p-6 pt-0 flex gap-3`}>
                                <button onClick={() => setShowContractModal(false)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>Cancelar</button>
                                <button onClick={saveContract} disabled={!contractForm.clientId || !contractForm.name || !contractForm.startDate} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50">
                                    {editingContract ? 'Guardar' : 'Crear Contrato'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* INVOICE MODAL */}
            <AnimatePresence>
                {showInvoiceModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInvoiceModal(false)}>
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className={`${t.modalBg} border ${t.modalBorder} rounded-3xl w-full max-w-md shadow-2xl`} onClick={e => e.stopPropagation()}>
                            <div className={`p-6 border-b ${t.modalBorder} flex items-center justify-between`}>
                                <h3 className={`text-lg font-bold ${t.textWhite}`}>Nueva Factura</h3>
                                <button onClick={() => setShowInvoiceModal(false)} className={`p-1 rounded-lg ${t.textMuted} hover:text-white`}><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className={labelClass}>Contrato *</label>
                                    <select value={invoiceForm.contractId || ''} onChange={e => {
                                        const ct = contracts.find(c => c.id === e.target.value);
                                        setInvoiceForm({ ...invoiceForm, contractId: e.target.value, clientId: ct?.clientId, subtotal: ct?.finalAmount || 0, currency: ct?.currency || 'MXN' });
                                    }} className={inputClass}>
                                        <option value="">Seleccionar contrato</option>
                                        {contracts.filter(c => c.status === 'active').map(c => <option key={c.id} value={c.id}>{c.name} ({formatCurrency(c.finalAmount, c.currency)})</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Período</label>
                                    <input value={invoiceForm.periodLabel || ''} onChange={e => setInvoiceForm({ ...invoiceForm, periodLabel: e.target.value })} className={inputClass} placeholder="Ej: Abril 2026" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Desde</label>
                                        <input type="date" value={invoiceForm.periodStart || ''} onChange={e => setInvoiceForm({ ...invoiceForm, periodStart: e.target.value })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Hasta</label>
                                        <input type="date" value={invoiceForm.periodEnd || ''} onChange={e => setInvoiceForm({ ...invoiceForm, periodEnd: e.target.value })} className={inputClass} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Subtotal</label>
                                        <input type="number" value={invoiceForm.subtotal || ''} onChange={e => setInvoiceForm({ ...invoiceForm, subtotal: parseFloat(e.target.value) || 0 })} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>IVA</label>
                                        <input type="number" value={invoiceForm.tax || ''} onChange={e => setInvoiceForm({ ...invoiceForm, tax: parseFloat(e.target.value) || 0 })} className={inputClass} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Fecha Límite de Pago *</label>
                                    <input type="date" value={invoiceForm.dueDate || ''} onChange={e => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} className={inputClass} />
                                </div>
                            </div>
                            <div className={`p-6 pt-0 flex gap-3`}>
                                <button onClick={() => setShowInvoiceModal(false)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>Cancelar</button>
                                <button onClick={saveInvoice} disabled={!invoiceForm.contractId || !invoiceForm.dueDate} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50">
                                    Crear Factura
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* PAYMENT MODAL */}
            <AnimatePresence>
                {showPaymentModal && paymentInvoice && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentModal(false)}>
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className={`${t.modalBg} border ${t.modalBorder} rounded-3xl w-full max-w-md shadow-2xl`} onClick={e => e.stopPropagation()}>
                            <div className={`p-6 border-b ${t.modalBorder}`}>
                                <h3 className={`text-lg font-bold ${t.textWhite}`}>Registrar Pago</h3>
                                <p className={`text-xs ${t.textMuted} mt-1`}>{paymentInvoice.invoiceNumber} • Saldo: {formatCurrency(paymentInvoice.total - paymentInvoice.paidAmount, paymentInvoice.currency)}</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className={labelClass}>Monto *</label>
                                    <input type="number" value={paymentForm.amount || ''} onChange={e => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })} className={inputClass} placeholder={String(paymentInvoice.total - paymentInvoice.paidAmount)} />
                                </div>
                                <div>
                                    <label className={labelClass}>Método de Pago</label>
                                    <select value={paymentForm.method} onChange={e => setPaymentForm({ ...paymentForm, method: e.target.value })} className={inputClass}>
                                        <option value="transfer">Transferencia bancaria</option>
                                        <option value="card">Tarjeta</option>
                                        <option value="cash">Efectivo</option>
                                        <option value="check">Cheque</option>
                                        <option value="other">Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Referencia / Folio</label>
                                    <input value={paymentForm.reference || ''} onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} className={inputClass} placeholder="Ej: Ref. bancaria 123456" />
                                </div>
                                <div>
                                    <label className={labelClass}>Fecha del Pago *</label>
                                    <input type="date" value={paymentForm.paymentDate || ''} onChange={e => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })} className={inputClass} />
                                </div>
                            </div>
                            <div className={`p-6 pt-0 flex gap-3`}>
                                <button onClick={() => setShowPaymentModal(false)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>Cancelar</button>
                                <button onClick={savePayment} disabled={!paymentForm.amount || !paymentForm.paymentDate} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-emerald-600/20 disabled:opacity-50">
                                    💰 Registrar Pago
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default BillingDashboard;
